import { DicomSeriesEndpoints } from './research/dicomSeriesEndpoints';
import { RepositoryAuditLog } from './research/RepositoryAuditLog';
import { RepositoryMembers } from './research/RepositoryMembers';
import { RepositoryProject } from './research/RepositoryProject';
import { RepositoryUpload } from './research/RepositoryUpload';
import { RepositoryForms } from './research/RepositoryForms';
import { RepositoryRoles } from './research/RepositoryRoles';
import { RepositorySites } from './research/RepositorySites';
import { RepositoryTools } from './research/RepositoryTools';
import { RepositoryUsers } from './research/RepositoryUsers';
import { ProjectPipelines } from './research/ProjectPipelines';
import { ProjectSubjects } from './research/ProjectSubjects';
import { ProjectMembers } from './research/ProjectMembers';
import { ProjectResults } from './research/ProjectResults';
import { ProjectResultsViews } from './research/ProjectResultsViews';
import { ProjectResultsReports } from './research/ProjectResultsReports';
import { ProjectSubject } from './research/ProjectSubject';
import { ProjectSites } from './research/ProjectSites';
import { Tasklist } from './research/Tasklist';
import { FilesUploaderStateManager } from './research/FilesUploaderStateManager';
import { Notifications } from './research/Notifications';

import { Vue } from 'vue-property-decorator';
import keyby from 'lodash.keyby';

export const Research = {
  modules: {
    repositoryAuditLog: RepositoryAuditLog,
    repositoryMembers: RepositoryMembers,
    repositoryProject: RepositoryProject,
    repositoryUpload: RepositoryUpload,
    repositoryRoles: RepositoryRoles,
    repositorySites: RepositorySites,
    repositoryUsers: RepositoryUsers,
    repositoryForms: RepositoryForms,
    tools: RepositoryTools, // TODO: reneme to repositoryTools for consistency
    results: ProjectResults, // TODO: reneme to repositoryResults for consistency
    projectPipelines: ProjectPipelines,
    projectSubjects: ProjectSubjects,
    projectSubject: ProjectSubject,
    projectMembers: ProjectMembers,
    projectSites: ProjectSites,
    seriesEndpoints: DicomSeriesEndpoints,
    tasklist: Tasklist,
    projectResultsViews: ProjectResultsViews,
    filesUploaderStateManager: FilesUploaderStateManager,
    projectResultsReports: ProjectResultsReports,
    notifications: Notifications,
  },

  state: {
    repository: {},
    projects: [],
    project: {},
    projectsLoaded: false,
    loadedByProject: {},
    projectLoadStartedRepositories: [],
  },

  getters: {
    getProject:
      (_, { projectsById }) =>
      (id) =>
        projectsById[id] ?? null,

    isProjectLoaded: (state) => (projectUuid) => !!state.loadedByProject[projectUuid],

    canApprovePipelines: ({ project }) => !!project?.can?.approvePipelines,

    canEditSubjects: ({ project }) => !!project?.can?.editSubjects,

    showQuickAccess: ({ project }) => !!project?.quickAccess?.length,

    projectsById: ({ projects }) => keyby(projects, 'id'),

    repositoryUuid: ({ repository }) => repository?.uuid,

    getCurrentRepository: (state) => state.repository,

    projectsLoaded: (state) => state.projectsLoaded,

    projectUuid: ({ project }) => project?.uuid,

    getCurrentProject: (state) => state.project,

    repository: ({ repository }) => repository,

    getProjects: (state) => state.projects,

    project: ({ project }) => project,
  },

  mutations: {
    setProjectLoadStartedRepositories(state, repoUuid) {
      state.projectLoadStartedRepositories = [...new Set([...state.projectLoadStartedRepositories, repoUuid])];
    },

    setLoadedProject(state, { projectUuid, value }) {
      Vue.set(state.loadedByProject, projectUuid, !!value);
    },

    addRepository(state, repository) {
      state.repository = repository;
    },

    setProjectsLoaded(state, value) {
      state.projectsLoaded = value;
    },

    addProjects(state, projects) {
      state.projects = projects;
    },

    addProject(state, project) {
      state.project = project;
    },
  },

  actions: {
    async getRepository({ commit }, repository) {
      const response = await this._vm.$api('repositories.show', {
        version: '1.0',
        repository,
      });
      commit('addRepository', response.data.data);
    },

    async getProjects({ commit }, repository) {
      commit('setProjectsLoaded', false);
      commit('setProjectLoadStartedRepositories', repository);
      const response = await this._vm.$api('repositories.projects.index', {
        version: '1.0',
        repository,
      });
      commit('setProjectsLoaded', true);
      commit('addProjects', response.data.data);
    },

    async getProject({ commit }, params) {
      let [repositoryUuid, projectUuid] = params;
      commit('setLoadedProject', { projectUuid });
      const response = await this._vm.$api('repositories.projects.show', {
        version: '1.0',
        repository: repositoryUuid,
        project: projectUuid,
      });
      commit('setLoadedProject', { projectUuid, value: true });
      commit('addProject', response.data.data);
    },

    async lazyLoadProjects({ dispatch, state }, repository) {
      if (!state.projectLoadStartedRepositories.includes(repository)) {
        dispatch('getProjects', repository);
      }
    },

    async toggleNextBlacklist({ dispatch }, { repository, blacklist }) {
      await this._vm.$api(
        'repositories.update',
        {
          repository,
        },
        { nextBlacklist: blacklist }
      );

      dispatch('getRepository', repository);
    },
  },
};
